import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import React, { useState } from "react";
import userPhotoDefault from "../../../assets/img/defaultUser.png";
import Dialog from "../../../components/common/Dialog";
import ShowImage from "../../../components/common/ShowImage";
import SubmitButton from "../../../components/common/SubmitButton";
import TagDriver from "../../../components/common/TagDriver";
import TextFieldOutlined from "../../../components/common/TextFieldOutlined";
import EntryEvent from "../../../components/EntryEvent/EntryEvent";
import useActionEvent from "../../../hooks/useActionEvent";
import useShowImage from "../../../hooks/useShowImage";
import { useTranslation } from "../../../hooks/useTranslation";
import { GuestType } from "../../../store/types/userTypes";
import { getResidentPassInformation } from "../../../utils/Parses";
import {UserState} from "../../../passapp-sdk/types/userTypes";

const useStyles = makeStyles({
  root: {
    boxShadow: "0px 10px 23px rgba(0, 0, 0, 0.12)",
  },
  buttonExit: {
    backgroundColor: "#ffc045",
  },
  spacingB: {
    paddingBlock: 8,
  },
  spacingI: {
    paddingInline: 15,
  },
  capitalize: {
    textTransform: "capitalize",
  },
  text: {
    fontSize: "0.9rem",
  },
  fullHeight: {
    height: "100%",
  },
});

type UserPreviewProps = {
  user: UserState;
  onEditUser: (userModify: UserState) => void;
  driver?: boolean;
};

const UserPreview = ({ user, driver, onEditUser }: UserPreviewProps) => {
  const classes = useStyles();
  const { sendIndividualEvent, loadingApprove } = useActionEvent();
  const { t, BundleKey } = useTranslation();
  const getImage = useShowImage();
  const [dialogEdit, setDialogEdit] = useState(false);
  const onRegisterExit = () => {
    sendIndividualEvent(user, "out", () => {
      user.isExit = true;
      onEditUser(user);
    });
  };

  const onEdit = (userModify: UserState) => {
    onEditUser(userModify);
    closeEditDialog();
  };

  const closeEditDialog = () => {
    setDialogEdit(false);
  };

  const openEditDialog = () => {
    setDialogEdit(true);
  };

  return (
    <Card className={classes.root}>
      <CardContent>
        <Grid container>
          <Grid item xs={12} sm={6} md={3}>
            <ShowImage
              image={getImage(user.facePhoto)}
              title=""
              defaultImage={userPhotoDefault}
              uploadNewPhoto={() => {}}
              circle
              small
              noAction
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Grid
              container
              direction="column"
              style={{ height: "100%", paddingInline: 10 }}
            >
              <Grid item className={classes.spacingB}>
                <TextFieldOutlined
                  required
                  small
                  name="firstname"
                  type="text"
                  label={t(BundleKey.DETAIL_PERSONALINF_FIRSTNAME)}
                  value={user.firstname}
                  disabled
                />
              </Grid>
              <Grid item className={classes.spacingB}>
                <TextFieldOutlined
                  required
                  small
                  name="lastname"
                  type="text"
                  label={t(BundleKey.DETAIL_PERSONALINF_LASTNAME)}
                  value={user.lastname}
                  disabled
                />
              </Grid>
              <Grid item className={classes.spacingB}>
                <TextFieldOutlined
                  required
                  small
                  name="countryCode"
                  type="text"
                  label={t(BundleKey.DETAIL_PERSONALINF_COUNTRY)}
                  value={user.countryCode}
                  disabled
                />
              </Grid>
              <Grid item className={classes.spacingB}>
                <TextFieldOutlined
                  required
                  small
                  name="dni"
                  type="text"
                  label={t(BundleKey.DETAIL_PERSONALINF_DNI)}
                  value={user.dni}
                  disabled
                />
              </Grid>
              <Grid item className={classes.spacingB}>
                <TagDriver driver={driver} small />
              </Grid>
            </Grid>
          </Grid>
          <Grid item sm={12} md={6}>
            <Grid container direction="column" className={classes.spacingI}>
              <Grid item className={classes.spacingB}>
                <Typography color="primary">
                  {t(BundleKey.DIALOG_MULTIPLEEXIT_DESTINATION_TITLE)}
                </Typography>
              </Grid>
              {user.authorizations?.map((pass) => (
                <Grid item className={classes.spacingB}>
                  <Grid container direction="row" alignItems="center">
                    <Grid item xs={8} style={{ paddingRight: 10 }}>
                      <Typography variant="body2">
                        {getResidentPassInformation(
                          pass.authorization.host,
                          pass.authorization.beginDatetime,
                          pass.authorization.endDatetime,
                          pass.authorization
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Grid container>
                        <Grid item xs={12} sm={6}>
                          <Button
                            color="primary"
                            variant={
                              pass.guestType === GuestType.any
                                ? "outlined"
                                : "text"
                            }
                            size="small"
                            className={classes.capitalize}
                          >
                            {t(BundleKey.DETAIL_AUTHORIZATION_PARTICULAR)}
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            color="primary"
                            variant={
                              pass.guestType === GuestType.provider
                                ? "outlined"
                                : "text"
                            }
                            size="small"
                            className={classes.capitalize}
                          >
                            {t(BundleKey.DETAIL_AUTHORIZATION_PROVIDER)}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
              <Grid item className={classes.spacingB}>
                <TextFieldOutlined
                  required
                  name="comment"
                  type="text"
                  label={t(BundleKey.DETAIL_ENTRYMETHOD_OBSERVATION_TITLE)}
                  value={user.comment}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Grid container justifyContent="flex-end" direction="row" spacing={2}>
          <Grid item xs={2} md={1}>
            <IconButton
              color="primary"
              component="span"
              onClick={openEditDialog}
              disabled={user.isExit}
            >
              <ErrorIcon />
            </IconButton>
          </Grid>
          <Grid item xs={10} sm={6} md={5}>
            <SubmitButton
              color="primary"
              size="large"
              className={classes.buttonExit}
              onClick={onRegisterExit}
              disabled={user.isExit}
              loading={loadingApprove}
            >
              {t(BundleKey.ACTION_EXIT)}
            </SubmitButton>
          </Grid>
        </Grid>
      </CardActions>
      <Dialog
        open={dialogEdit}
        onClose={closeEditDialog}
        fullScreen
        title={t(BundleKey.DETAIL_MAIN_TITLE_EXIT)}
      >
        <EntryEvent
          config={{ eventType: "out", editMode: true }}
          initialUser={user}
          stateTunnel={onEdit}
        />
      </Dialog>
    </Card>
  );
};

export default UserPreview;
